import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Shield, AlertTriangle } from 'lucide-react';
import { hasPermission as checkRBACPermission } from '../config/roles';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermissions?: string[];
  requiredRoles?: string[];
  menuName?: string;
  resource?: string; // RBAC resource name (e.g., 'suppliers', 'cameras')
  action?: string; // RBAC action (default: 'view')
  fallback?: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermissions = [],
  requiredRoles = [],
  menuName,
  resource,
  action = 'view',
  fallback
}) => {
  // hasPermission eksikti — eklendi. (isLoading varsa onu da değerlendirmek iyi olur.)
  const { user, hasMenuPermission, hasRole, hasPermission, isLoading } = useAuth();

  // Auth hâlâ yükleniyorsa flash of "yetkisiz" yaşamamak için
  if (isLoading) {
    return <div className="min-h-[200px] flex items-center justify-center text-gray-500">Yükleniyor…</div>;
  }

  // Rolleri/izinleri normalize et (harf büyük/küçük farkını önlemek için)
  const norm = (s: string) => s?.trim().toLowerCase();
  const userRoles = Array.isArray(user?.roles)
    ? user!.roles.map(norm)
    : user?.role
      ? [norm(user.role)]
      : [];

  const requiredRolesNorm = requiredRoles.map(norm);
  const requiredPermsNorm = requiredPermissions.map(norm);

  // Rol kontrolü
  const hasRequiredRole =
    requiredRolesNorm.length === 0 ||
    requiredRolesNorm.some((role) => hasRole ? hasRole(role) : userRoles.includes(role));

  // Menü izni (old system) veya RBAC (new system)
  const hasMenuAccess = !menuName || (hasMenuPermission && hasMenuPermission(menuName));

  // RBAC-based access check (yeni sistem)
  // Eğer resource belirtilirse, RBAC'dan kontrol et
  // Eğer belirtilmezse, menü sistemini kullan
  const hasRBACAccess = !resource || (user && user.role ? checkRBACPermission(user.role as any, resource as any, action as any) : false);

  // Spesifik izinler
  const hasRequiredPerms =
    requiredPermsNorm.length === 0 ||
    (hasPermission ? requiredPermsNorm.every((p) => hasPermission(p)) : false);

  // Accessible if: (menuName check passes OR resource check passes) AND other checks pass
  // Bu sayede resource varsa RBAC kullan, yoksa menü sistemini kullan
  const hasAccess = ((!menuName && !resource) || hasMenuAccess || hasRBACAccess);

  if (!hasRequiredRole || !hasAccess || !hasRequiredPerms) {
    if (fallback) return <>{fallback}</>;
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Erişim Reddedildi</h3>
          <p className="text-gray-600 mb-4">Bu sayfaya erişim için gerekli yetkiniz bulunmuyor.</p>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800 text-left">
                <p className="font-medium mb-1">Gerekli Yetkiler:</p>
                {requiredRolesNorm.length > 0 && (
                  <p className="mb-1">
                    <strong>Roller:</strong> {requiredRolesNorm.join(', ')}
                  </p>
                )}
                {requiredPermsNorm.length > 0 && (
                  <p>
                    <strong>İzinler:</strong> {requiredPermsNorm.join(', ')}
                  </p>
                )}
                <p className="mt-2">
                  <strong>Mevcut Rolünüz:</strong> {user?.role || 'Bilinmiyor'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;